// src/cockpit4/components/ProjectPortfolioManager.tsx
// ============================================
// 项目组合管理（项目中心 P2-功能8）- v2.0 详细改进计划
// 项目优先级排序 / 组合健康度 / 资源组合优化 / 投资回报分析 / 组合平衡
// 数据来源：localStorage（xeaos_project_portfolio）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { FolderKanban, TrendingUp, Target, Scale, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

interface PortfolioProject {
  id: string;
  name: string;
  category: string;
  value: number;
  cost: number;
  risk: number;
  roi: number;
  health: number;
  priority: number;
  stage: "进行中" | "规划中" | "已完成";
}

const INITIAL: PortfolioProject[] = [
  { id: "p1", name: "XEAOS 核心系统", category: "核心产品", value: 100, cost: 60, risk: 35, roi: 1.67, health: 88, priority: 1, stage: "进行中" },
  { id: "p2", name: "AI 营销增长", category: "增长", value: 80, cost: 40, risk: 50, roi: 2.0, health: 82, priority: 2, stage: "进行中" },
  { id: "p3", name: "量化交易系统", category: "新业务", value: 120, cost: 90, risk: 70, roi: 1.33, health: 65, priority: 4, stage: "进行中" },
  { id: "p4", name: "知识资产平台", category: "生态", value: 60, cost: 30, risk: 45, roi: 2.0, health: 75, priority: 3, stage: "规划中" },
  { id: "p5", name: "客户成功体系", category: "增长", value: 50, cost: 25, risk: 30, roi: 2.0, health: 90, priority: 2, stage: "已完成" },
];

const KEY = "xeaos_project_portfolio";

function load(): PortfolioProject[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as PortfolioProject[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function ProjectPortfolioManager() {
  const [projects, setProjects] = useState<PortfolioProject[]>(load);

  const stats = useMemo(() => {
    const totalValue = projects.reduce((a, p) => a + p.value, 0);
    const totalCost = projects.reduce((a, p) => a + p.cost, 0);
    const avgHealth = projects.reduce((a, p) => a + p.health, 0) / projects.length;
    const activeCount = projects.filter((p) => p.stage === "进行中").length;
    return { totalValue, totalCost, avgHealth, activeCount };
  }, [projects]);

  const sorted = useMemo(() => [...projects].sort((a, b) => a.priority - b.priority), [projects]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <FolderKanban size={14} className="text-blue-400" />
        <h3 className="text-[12px] font-medium text-white">项目组合管理</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能8 · Portfolio</span>
        <button
          onClick={() => setProjects((prev) => prev.map((p) => ({ ...p, health: Math.min(100, p.health + 1) })))}
          className="flex items-center gap-1 ml-auto px-2 py-1 rounded-md border border-white/[0.06] bg-white/[0.02] text-[10px] text-gray-400 hover:text-white"
        >
          <RefreshCw size={10} /> 刷新
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <MetricCard label="组合总价值" value={stats.totalValue.toFixed(0)} color="#34D399" />
        <MetricCard label="组合总成本" value={stats.totalCost.toFixed(0)} color="#F59E0B" />
        <MetricCard label="组合健康度" value={`${stats.avgHealth.toFixed(0)}/100`} color="#38BDF8" />
        <MetricCard label="进行中项目" value={`${stats.activeCount} 个`} color="#A78BFA" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">投资回报分析 · 价值 vs 成本</span>
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={projects.map((p) => ({ name: p.name.slice(0, 8), 价值: p.value, 成本: p.cost }))} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 8, fontFamily: "monospace" }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} width={36} />
                <Tooltip contentStyle={{ background: "#0a0e1a", border: "1px solid #1e2a3f", borderRadius: 6, fontSize: 11, fontFamily: "monospace" }} labelStyle={{ color: "#E5E7EB" }} />
                <Bar dataKey="价值" fill="#34D399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="成本" fill="#F59E0B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <Target size={11} className="text-blue-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">优先级排序 · 价值/成本/风险</span>
          </div>
          <div className="space-y-1.5">
            {sorted.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                <span className={`w-5 text-center text-[10px] font-mono font-bold ${p.priority <= 2 ? "text-emerald-400" : p.priority === 3 ? "text-amber-400" : "text-red-400"}`}>P{p.priority}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] text-white truncate">{p.name}</div>
                  <div className="text-[8px] font-mono text-gray-600">{p.category} · ROI {p.roi.toFixed(2)}x · 风险 {p.risk}%</div>
                </div>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${p.health >= 80 ? "bg-emerald-500/15 text-emerald-400" : p.health >= 70 ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"}`}>{p.health}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-[9px] font-mono text-cyan-400">
            <Scale size={10} className="mt-0.5 flex-shrink-0" />
            组合平衡建议：量化系统风险 70% 偏高 → 建议控制投入比例；核心产品价值贡献最大 → 保持资源倾斜
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

